import _ from 'lodash'
import Vue from 'vue'
import VueCompositionAPI from '@vue/composition-api'
import App from './app.vue'
import Router from 'vue-router'
import axios from 'axios'
import path from 'path'
import fs from 'fs'
import VueSession from 'vue-session'
import VueRouterBackButton from 'vue-router-back-button'

import AuthService from '@/services/authService'
import SocketService from '@/services/socketService'
import MapService from '@/services/mapService'

// Modules
import AdminModule from '@/modules/admin'
import DeveloperModule from '@/modules/developer'
import ContactModule from '@/modules/contacts'
import CommunicationModule from '@/modules/communication'
import InventoryModule from '@/modules/inventory'
import OrdersModule from '@/modules/orders'
import OrderEditorModule from '@/modules/ordereditor'
import PricingModule from '@/modules/pricing'
import PurchasingModule from '@/modules/purchasing'
import CoreModule from '@/modules/core'
import UserModule from '@/modules/users'
import AutomationModule from '@/modules/automations'
import ReportingModule from '@/modules/reporting'
import IntelligenceModule from '@/modules/intelligence'
import DespatchModule from '@/modules/despatch'
import OptimiserModule from '@/modules/optimiser'
import ProductionModule from '@/modules/production'
import TasksModule from '@/modules/tasks'
import AccountsModule from '@/modules/accounts'
import SupportModule from '@/modules/support'

// Apps
import GlassDesignerApp from '@/apps/glass-designer/src'
import GlassProductionStationApp from '@/apps/glass-production-station/src'

import Default from '@/layouts/default.vue'
import Blank from '@/layouts/blank.vue'
import LegalLayout from '@/layouts/legal.vue'
import LoadingLayout from '@/layouts/loading.vue'
import MobileLayout from '@/layouts/mobile.vue'

import store from '@/store'


Vue.config.productionTip = false


// Global Mixins
import formatter from '@/mixins/formatter'
import messagebox from '@/mixins/messagebox'
import account from '@/mixins/account'
import permission from '@/mixins/permission'
import integration from '@/mixins/integration'
import modalDisplay from '@/mixins/modalDisplay'
import processing from '@/mixins/processing'
import uuid from '@/mixins/uuid'
import utils from '@/mixins/utils'
import parseUtils from '@/mixins/parseUtils'
import page from '@/mixins/page'
import socketService from '@/mixins/socketService'
import scrollTo from '@/mixins/scrollTo'
import validationMixin from '@/mixins/validation'
import developer from '@/mixins/developer'

Vue.mixin(formatter)
Vue.mixin(messagebox)
Vue.mixin(account)
Vue.mixin(permission)
Vue.mixin(integration)
Vue.mixin(modalDisplay)
Vue.mixin(processing)
Vue.mixin(uuid)
Vue.mixin(utils)
Vue.mixin(parseUtils)
Vue.mixin(page)
Vue.mixin(socketService)
Vue.mixin(scrollTo)
Vue.mixin(validationMixin)
Vue.mixin(developer)

Vue.component('DefaultLayout', Default)
Vue.component('BlankLayout', Blank)
Vue.component('LegalLayout', LegalLayout)
Vue.component('LoadingLayout', LoadingLayout)
Vue.component('MobileLayout', MobileLayout)

// Modules
// Router needs these modules setup & adding routes before Vue is installed
Vue.use(Router)
const moduleCore = new CoreModule()
const moduleAdmin = new AdminModule()
const moduleDeveloper = new DeveloperModule()
const moduleContact = new ContactModule()
const moduleCommunication = new CommunicationModule()
const moduleInventory = new InventoryModule()
const moduleOrders = new OrdersModule()
const moduleOrderEditor = new OrderEditorModule()
const modulePricing = new PricingModule()
const modulePurchasing = new PurchasingModule()
const moduleUser = new UserModule()
const moduleAutomation = new AutomationModule()
const moduleReporting = new ReportingModule()
const moduleIntelligence = new IntelligenceModule()
const moduleDespatch = new DespatchModule()
const moduleProduction = new ProductionModule()
const moduleOptimiser = new OptimiserModule()
const moduleTasks = new TasksModule()
const moduleAccounts = new AccountsModule()
const moduleSupport = new SupportModule()

const appGlassDesigner = new GlassDesignerApp()
const appGlassProductionStation = new GlassProductionStationApp()

// Router
let router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: _.concat(
    moduleCore.getRoutes(),
    moduleAdmin.getRoutes(),
    moduleDeveloper.getRoutes(),
    moduleContact.getRoutes(),
    moduleCommunication.getRoutes(),
    moduleInventory.getRoutes(),
    moduleOrders.getRoutes(),
    moduleOrderEditor.getRoutes(),
    modulePricing.getRoutes(),
    modulePurchasing.getRoutes(),
    moduleUser.getRoutes(),
    moduleAutomation.getRoutes(),
    moduleIntelligence.getRoutes(),
    moduleDespatch.getRoutes(),
    moduleProduction.getRoutes(),
    moduleOptimiser.getRoutes(),
    moduleTasks.getRoutes(),
    moduleAccounts.getRoutes(),
    moduleSupport.getRoutes(),

    appGlassDesigner.getRoutes(),
    appGlassProductionStation.getRoutes()
  )
})

Vue.use(VueCompositionAPI)
Vue.use(VueRouterBackButton, { router, ignoreRoutesWithSameName: true })

const authService = new AuthService()

router.beforeEach((to, from, next) => { 
  document.title = to.meta.title + " - Synergy Software Pty Ltd"

  // Clear page error
  store.commit('page/setPageError', null)

  store.commit("page/setPageTitle", to.meta.title)
  store.commit("page/setPageBreadcrumbs", [])

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authService.hasAuthSession()) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else {
      next()
    }
  } else {
    if (authService.hasAuthSession()) {
      if(to.matched.some(record => record.meta.noRedirectForAuth)) {
        next()
      } else {
        next({
          path: '/',
          params: { nextUrl: to.fullPath }
        })
      }
    } else {
      next()
    }
  }
})


/**
 * Axios Setup helper
 */
function setupAPIClient(Vue, options = {}) {
  let axiosClient = axios.create(options)

  // Setup interceptors
  axiosClient.interceptors.response.use(
    (response)=> {
      // Refresh auth token
      try {
        var authToken = response.headers['authorization']
        if(authToken != undefined) {
          var tokens = authToken.split(' ')
          Vue.prototype.$authService.setAuthToken(tokens[1].trim())
        }
        var proxyAccess = response.headers['x-proxy-access']
        if(proxyAccess !== undefined && parseInt(proxyAccess) == 1) {
          store.commit('system/setUserProxyAccess', true)
        } else {
          store.commit('system/setUserProxyAccess', false)
        }
      } catch { }

      return Promise.resolve(response)
    },
    (error)=> {
      if(error.response && error.response.status===401) {
        Vue.prototype.$authService.clearAuthSession()
        router.push('/login'); 
      }
      return Promise.reject(error);
    }
  );

  axiosClient.interceptors.request.use(
    (config) => {
      if(Vue.prototype.$authService) {
        let token = Vue.prototype.$authService.getAuthToken()
        if (token)
          config.headers['Authorization'] = `Bearer ${ token }`;
      }
      return config;
    }, 
  
    (error) => {
      return Promise.reject(error);
    }
  );

  return axiosClient
}


// Middleware
Vue.use(VueSession, {
  persist: true
})
Vue.use({
  install (Vue) {
    
    Vue.prototype.$api = setupAPIClient(Vue, {
      baseURL: process.env.VUE_APP_API_URL,
      withCredentials: true,
      credentials: 'same-origin'
    })
    Vue.prototype.$reportingApi = setupAPIClient(Vue, {
      baseURL: process.env.VUE_APP_REPORTING_API_URL || process.env.VUE_APP_API_URL,
      withCredentials: true,
      credentials: 'same-origin'
    })

    Vue.prototype.$authService = new AuthService()
    Vue.prototype.$socketService = new SocketService(process.env.VUE_APP_SOCKET_URL)
    Vue.prototype.$mapService = new MapService()

    Vue.prototype.$apiService = {}

    // Install module services
    Vue.prototype.$moduleAdmin = moduleAdmin
    Vue.prototype.$moduleDeveloper = moduleDeveloper
    Vue.prototype.$moduleContact = moduleContact
    Vue.prototype.$moduleCommunication = moduleCommunication
    Vue.prototype.$moduleInventory = moduleInventory
    Vue.prototype.$moduleOrders = moduleOrders
    Vue.prototype.$moduleOrderEditor = moduleOrderEditor
    Vue.prototype.$modulePricing = modulePricing
    Vue.prototype.$modulePurchasing = modulePurchasing
    Vue.prototype.$moduleCore = moduleCore
    Vue.prototype.$moduleUser = moduleUser
    Vue.prototype.$moduleAutomation = moduleAutomation
    Vue.prototype.$moduleReporting = moduleReporting
    Vue.prototype.$moduleIntelligence = moduleIntelligence
    Vue.prototype.$moduleDespatch = moduleDespatch
    Vue.prototype.$moduleProduction = moduleProduction
    Vue.prototype.$moduleOptimiser = moduleOptimiser
    Vue.prototype.$moduleTasks = moduleTasks
    Vue.prototype.$moduleAccounts = moduleAccounts
    Vue.prototype.$moduleSupport = moduleSupport

    Vue.prototype.$moduleAdmin.install(Vue)
    Vue.prototype.$moduleDeveloper.install(Vue)
    Vue.prototype.$moduleContact.install(Vue)
    Vue.prototype.$moduleCommunication.install(Vue)
    Vue.prototype.$moduleInventory.install(Vue)
    Vue.prototype.$moduleOrders.install(Vue)
    Vue.prototype.$moduleOrderEditor.install(Vue)
    Vue.prototype.$modulePricing.install(Vue)
    Vue.prototype.$modulePurchasing.install(Vue)
    Vue.prototype.$moduleCore.install(Vue)
    Vue.prototype.$moduleUser.install(Vue)
    Vue.prototype.$moduleAutomation.install(Vue)
    Vue.prototype.$moduleReporting.install(Vue)
    Vue.prototype.$moduleIntelligence.install(Vue)
    Vue.prototype.$moduleDespatch.install(Vue)
    Vue.prototype.$moduleProduction.install(Vue)
    Vue.prototype.$moduleOptimiser.install(Vue)
    Vue.prototype.$moduleTasks.install(Vue)
    Vue.prototype.$moduleAccounts.install(Vue)
    Vue.prototype.$moduleSupport.install(Vue)

    // Install app services
    Vue.prototype.$appGlassDesigner = appGlassDesigner
    Vue.prototype.$appGlassProductionStation = appGlassProductionStation

    Vue.prototype.$appGlassDesigner.install(Vue, { store: store })
    Vue.prototype.$appGlassProductionStation.install(Vue, { store: store })

    // Connect socket service
    Vue.prototype.$socketService.connect()

    // Glass global functions / variables
    Vue.prototype.$glass = {
      apiUrl: process.env.VUE_APP_API_URL,
      reportingApiUrl: process.env.VUE_APP_REPORTING_API_URL || process.env.VUE_APP_API_URL,
      socketUrl: process.env.VUE_APP_SOCKET_URL,
      isLiveSite: parseInt(process.env.VUE_APP_LIVE || 0) != 0,
      isLocalSite: parseInt(process.env.VUE_APP_LOCAL || 0) != 0,
      isDevelopmentBuild: parseInt(process.env.VUE_APP_LIVE || 0) == 0
    };

    
  }
})


// Plugins
import VCalendar from 'v-calendar'
import Vue2TouchEvents from 'vue2-touch-events'
import VueLocalStorage from 'vue-localstorage'
import { VueReCaptcha } from 'vue-recaptcha-v3'


Vue.use(VCalendar, {
  firstDayOfWeek: 2,  // Monday
  locale: 'en-AU'
});
Vue.use(Vue2TouchEvents)
Vue.use(VueLocalStorage)

// ReCaptcha Support
// Used for public login / registration requests
Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_GOOGLE_RECAPTCHA_API_KEY,
  loaderOptions: {
    autoHideBadge: true
  }
})



// Global Components
import FormGroup from '@/components/form/formGroup'
import FormRow from '@/components/form/formRow'
import FormRowFull from '@/components/form/formRowFull'

import FormAccountSetting from '@/components/form/formAccountSetting'
import FormWizard from '@/components/form/formWizard'
import FormWizardStep from '@/components/form/formWizardStep'
import FormWizardButton from '@/components/form/formWizardButton'
import FormInputText from '@/components/form/formInputText'
import FormInputTextArea from '@/components/form/formInputTextArea'
import FormInputAddress from '@/components/form/formInputAddress'
import FormInputAddressSearch from '@/components/form/formInputAddressSearch'
import FormInputEmail from '@/components/form/formInputEmail'
import FormSelect from '@/components/form/formSelect'
import FormOptionGroup from '@/components/form/formOptionGroup'
import FormOption from '@/components/form/formOption'
import FormCheckbox from '@/components/form/formCheckbox'
import FormHorizontalRule from '@/components/form/formHorizontalRule'
import FormSubmit from '@/components/form/formSubmit'
import FormUpload from '@/components/form/formUpload'
import FormUploadDropTarget from '@/components/form/formUploadDropTarget'
import FormButton from '@/components/form/formButton'
import FormLabelTag from '@/components/form/formLabelTag'
import FormDate from '@/components/form/formDate'
import FormDateTime from '@/components/form/formDateTime'
import FormTime from '@/components/form/formTime'
import FormDaysWeek from '@/components/form/formDaysWeek'
import FormInputCurrency from '@/components/form/formInputCurrency'
import FormMultiSelect from '@/components/form/formMultiSelect'
import FormColourTag from '@/components/form/formColourTag'
import FormSearchableSelect from '@/components/form/formSearchableSelect'
import FormStaticLabel from '@/components/form/formStaticLabel'
import FormSwitch from '@/components/form/formSwitch'
import FormTextEditor from '@/components/form/formTextEditor'
import FormNote from '@/components/form/formNote'
import FormPanel from '@/components/form/formPanel'
import FormImageCropper from '@/components/form/formImageCropper'
import FormLocationStateSelect from '@/components/form/formLocationStateSelect'
import FormUpdateFilter from '@/components/form/formUpdateFilter'
import FormLookupTable from '@/components/form/formLookupTable'

import LinkAutomation from '@/components/links/linkAutomation'
import LinkUser from '@/components/links/linkUser'
import LinkUserGroup from '@/components/links/linkUserGroup'
import LinkStock from '@/components/links/linkStock'
import LinkContact from '@/components/links/linkContact'
import LinkOrder from '@/components/links/linkOrder'
import LinkPurchaseOrder from '@/components/links/linkPurchaseOrder'
import LinkProductionStage from '@/components/links/linkProductionStage'
import LinkProductionStation from '@/components/links/linkProductionStation'
import LinkProductionPanel from '@/components/links/linkProductionPanel'
import LinkTougheningBatch from '@/components/links/linkTougheningBatch'
import LinkOptimiserSheet from '@/components/links/linkOptimiserSheet'
import LinkResourceGroup from '@/components/links/linkResourceGroup'
import LinkResource from '@/components/links/linkResource'
import LinkPriceRequest from '@/components/links/linkPriceRequest'
import LinkStockTake from '@/components/links/linkStockTake'
import LinkStockCategory from '@/components/links/linkStockCategory'
import LinkStockPriceGroup from '@/components/links/linkStockPriceGroup'
import LinkTask from '@/components/links/linkTask'
import LinkInvoice from '@/components/links/linkInvoice'
import LinkPaymentReceived from '@/components/links/linkPaymentReceived'
import LinkOverPayment from '@/components/links/linkOverPayment'
import LinkCreditNote from '@/components/links/linkCreditNote'
import LinkGoogleMap from '@/components/links/linkGoogleMap'
import LinkIntegrationLink from '@/components/links/linkIntegrationLink'

import Icon from '@/components/icons/icon'
import IconBoolean from '@/components/icons/iconBoolean'

import LabelTags from '@/components/labelTags'
import LabelBadge from '@/components/labelBadge'
import LabelDespatchBadge from '@/components/labelDespatchBadge'

import CurrencyValue from '@/components/values/currencyValue'
import UOMValue from '@/components/values/uomValue'

import ActionButton from '@/components/buttons/actionButton'
import AppButton from '@/components/buttons/appButton.vue'
import ButtonGroup from '@/components/buttons/buttonGroup.vue'
import LinkButton from '@/components/buttons/linkButton'
import ExpandCollapseButton from '@/components/buttons/expandCollapseButton'
import PaginatedButtonGroup from '@/components/buttons/paginatedButtonGroup'
import ClipboardCopyButton from '@/components/buttons/clipboardCopyButton'


import Tooltip from '@/components/tooltip'
import ActivityIndicator from '@/components/activityIndicator'
import DataItem from '@/components/dataItem'
import Spinner from '@/components/spinner'
import NotificationBar from '@/components/notificationBar'
import NotificationBarTitle from '@/components/notificationBarTitle'
import NotificationBarItem from '@/components/notificationBarItem'
import NotificationBarButton from '@/components/notificationBarButton'
import HelpPopover from '@/components/helpPopover'
import InlineHelpPopover from '@/components/inlineHelpPopover'
import FileAttachment from '@/components/fileAttachment'
import AddressDisplay from '@/components/addressDisplay.vue'
import SVGPreview from '@/components/svgPreview.vue'
import LoadingPanel from '@/components/loadingPanel'
import ItemPanelList from '@/components/itemPanelList'
import ItemPanel from '@/components/itemPanel'
import ItemPanelData from '@/components/itemPanelData'
import Scroller from '@/components/scroller'

import ContentBox from '@/components/layout/contentBox'
import ContentTitle from '@/components/layout/contentTitle'
import ContentSubTitle from '@/components/layout/contentSubTitle'
import DataPageArea from '@/components/layout/dataPageArea'
import OverviewActionBox from '@/components/layout/overviewActionBox'
import OverviewActionButton from '@/components/layout/overviewActionButton'
import OverviewActionDivider from '@/components/layout/overviewActionDivider'
import OverviewBox from '@/components/layout/overviewBox'
import OverviewBoxGroup from '@/components/layout/overviewBoxGroup'
import OverviewBoxInfoItem from '@/components/layout/overviewBoxInfoItem'
import OverviewBoxList from '@/components/layout/overviewBoxList'
import OverviewBoxListItem from '@/components/layout/overviewBoxListItem'


import TabbedBox from '@/components/layout/tabbedBox'
import Tab from '@/components/layout/tab'
import TabPage from '@/components/layout/tabPage'
import NestedTabbedBox from '@/components/layout/nestedTabbedBox'
import SkeletonBox from '@/components/layout/skeletonBox'
import SkeletonCircle from '@/components/layout/skeletonCircle'


import ActionDropMenu from '@/components/actionDropMenu'
import DropdownMenuPopover from '@/components/dropdownMenuPopover'
import DropdownMenu from '@/components/dropdownMenu'
import DropdownMenuItem from '@/components/dropdownMenuItem'
import DropdownMenuLabel from '@/components/dropdownMenuLabel'
import DropdownMenuDivider from '@/components/dropdownMenuDivider'
import DropdownSubMenu from '@/components/dropdownSubMenu'
import TableActionItem from '@/components/table/tableActionItem'

import ActivityLogStream from '@/components/activityLogStream'

import CollapsibleTable from '@/components/table/collapsibleTable'
import CollapsibleRow from '@/components/table/collapsibleRow'
import CollapsibleRowArrow from '@/components/table/collapsibleRowArrow'

import DataTable from '@/components/table/dataTable'
import DataTableTopHeaderRow from '@/components/table/dataTableTopHeaderRow'
import DataTableHeaderCell from '@/components/table/dataTableHeaderCell'

import StaticDataTable from '@/components/table/staticDataTable'

// Mobile
import MobilePage from '@/components/mobile/mobilePage'
import MobileContentBox from '@/components/mobile/mobileContentBox'
import MobileNavBar from '@/components/mobile/mobileNavBar'
import MobileDrawer from '@/components/mobile/mobileDrawer'
import MobileDashboardButton from '@/components/mobile/mobileDashboardButton'
import MobileTable from '@/components/mobile/mobileTable'
import MobileTableCell from '@/components/mobile/mobileTableCell'
import MobileTableSectionHeader from '@/components/mobile/mobileTableSectionHeader'
import MobileTabbedPage from '@/components/mobile/mobileTabbedPage'
import MobileTabContent from '@/components/mobile/mobileTabContent'
import MobileTab from '@/components/mobile/mobileTab'
import MobileToggleButton from '@/components/mobile/mobileToggleButton'


Vue.component('FormGroup', FormGroup)
Vue.component('FormRow', FormRow)
Vue.component('FormRowFull', FormRowFull)
Vue.component('FormAccountSetting', FormAccountSetting)
Vue.component('FormWizard', FormWizard)
Vue.component('FormWizardStep', FormWizardStep)
Vue.component('FormWizardButton', FormWizardButton)
Vue.component('FormInputText', FormInputText)
Vue.component('FormInputTextArea', FormInputTextArea)
Vue.component('FormInputAddress', FormInputAddress)
Vue.component('FormInputAddressSearch', FormInputAddressSearch)
Vue.component('FormInputEmail', FormInputEmail)
Vue.component('FormSelect', FormSelect)
Vue.component('FormOptionGroup', FormOptionGroup)
Vue.component('FormOption', FormOption)
Vue.component('FormCheckbox', FormCheckbox)
Vue.component('FormHorizontalRule', FormHorizontalRule)
Vue.component('FormSubmit', FormSubmit)
Vue.component('FormUpload', FormUpload)
Vue.component('FormUploadDropTarget', FormUploadDropTarget)
Vue.component('FormButton', FormButton)
Vue.component('FormLabelTag', FormLabelTag)
Vue.component('FormDate', FormDate)
Vue.component('FormDateTime', FormDateTime)
Vue.component('FormTime', FormTime)
Vue.component('FormDaysWeek', FormDaysWeek)
Vue.component('FormInputCurrency', FormInputCurrency)
Vue.component('FormMultiSelect', FormMultiSelect)
Vue.component('FormColourTag', FormColourTag)
Vue.component('FormSearchableSelect', FormSearchableSelect)
Vue.component('FormStaticLabel', FormStaticLabel)
Vue.component('FormSwitch', FormSwitch)
Vue.component('FormTextEditor', FormTextEditor)
Vue.component('FormNote', FormNote)
Vue.component('FormPanel', FormPanel)
Vue.component('FormImageCropper', FormImageCropper)
Vue.component('FormLocationStateSelect', FormLocationStateSelect)
Vue.component('FormUpdateFilter', FormUpdateFilter)
Vue.component('FormLookupTable', FormLookupTable)

Vue.component('LinkButton', LinkButton)
Vue.component('LinkAutomation', LinkAutomation)
Vue.component('LinkUser', LinkUser)
Vue.component('LinkUserGroup', LinkUserGroup)
Vue.component('LinkStock', LinkStock)
Vue.component('LinkContact', LinkContact)
Vue.component('LinkOrder', LinkOrder)
Vue.component('LinkPurchaseOrder', LinkPurchaseOrder)
Vue.component('LinkProductionStage', LinkProductionStage)
Vue.component('LinkProductionStation', LinkProductionStation)
Vue.component('LinkProductionPanel', LinkProductionPanel)
Vue.component('LinkTougheningBatch', LinkTougheningBatch)
Vue.component('LinkOptimiserSheet', LinkOptimiserSheet)
Vue.component('LinkResourceGroup', LinkResourceGroup)
Vue.component('LinkResource', LinkResource)
Vue.component('LinkPriceRequest', LinkPriceRequest)
Vue.component('LinkStockTake', LinkStockTake)
Vue.component('LinkStockCategory', LinkStockCategory)
Vue.component('LinkStockPriceGroup', LinkStockPriceGroup)
Vue.component('LinkTask', LinkTask)
Vue.component('LinkInvoice', LinkInvoice)
Vue.component('LinkPaymentReceived', LinkPaymentReceived)
Vue.component('LinkOverPayment', LinkOverPayment)
Vue.component('LinkCreditNote', LinkCreditNote)
Vue.component('LinkGoogleMap', LinkGoogleMap)
Vue.component('LinkIntegrationLink', LinkIntegrationLink)
Vue.component('Icon', Icon)
Vue.component('IconBoolean', IconBoolean)

Vue.component('LabelBadge', LabelBadge)
Vue.component('LabelTags', LabelTags)
Vue.component('LabelDespatchBadge', LabelDespatchBadge)

Vue.component('CurrencyValue', CurrencyValue)
Vue.component('UOMValue', UOMValue)

Vue.component('Tooltip', Tooltip)
Vue.component('ActionButton', ActionButton)
Vue.component('ActivityIndicator', ActivityIndicator)
Vue.component('DataPageArea', DataPageArea)
Vue.component('DataItem', DataItem)
Vue.component('Spinner', Spinner)
Vue.component('NotificationBar', NotificationBar)
Vue.component('NotificationBarTitle', NotificationBarTitle)
Vue.component('NotificationBarItem', NotificationBarItem)
Vue.component('NotificationBarButton', NotificationBarButton)
Vue.component('HelpPopover', HelpPopover)
Vue.component('InlineHelpPopover', InlineHelpPopover)
Vue.component('FileAttachment', FileAttachment)
Vue.component('AddressDisplay', AddressDisplay)
Vue.component('SVGPreview', SVGPreview)
Vue.component('AppButton', AppButton)
Vue.component('ButtonGroup', ButtonGroup)
Vue.component('LoadingPanel', LoadingPanel)
Vue.component('ItemPanelList', ItemPanelList)
Vue.component('ItemPanel', ItemPanel)
Vue.component('ItemPanelData', ItemPanelData)
Vue.component('Scroller', Scroller)
Vue.component('ExpandCollapseButton', ExpandCollapseButton)
Vue.component('PaginatedButtonGroup', PaginatedButtonGroup)
Vue.component('ClipboardCopyButton', ClipboardCopyButton)

Vue.component('TabbedBox', TabbedBox)
Vue.component('Tab', Tab)
Vue.component('TabPage', TabPage)
Vue.component('NestedTabbedBox', NestedTabbedBox)
Vue.component('SkeletonBox', SkeletonBox)
Vue.component('SkeletonCircle', SkeletonCircle)

Vue.component('OverviewActionBox', OverviewActionBox)
Vue.component('OverviewActionButton', OverviewActionButton)
Vue.component('OverviewActionDivider', OverviewActionDivider)
Vue.component('OverviewBox', OverviewBox)
Vue.component('OverviewBoxGroup', OverviewBoxGroup)
Vue.component('OverviewBoxInfoItem', OverviewBoxInfoItem)
Vue.component('OverviewBoxList', OverviewBoxList)
Vue.component('OverviewBoxListItem', OverviewBoxListItem)

Vue.component('ActionDropMenu', ActionDropMenu)
Vue.component('DropdownMenuPopover', DropdownMenuPopover)
Vue.component('DropdownMenu', DropdownMenu)
Vue.component('DropdownMenuItem', DropdownMenuItem)
Vue.component('DropdownMenuLabel', DropdownMenuLabel)
Vue.component('DropdownMenuDivider', DropdownMenuDivider)
Vue.component('DropdownSubMenu', DropdownSubMenu)
Vue.component('TableActionItem', TableActionItem)

Vue.component('ActivityLogStream', ActivityLogStream)

Vue.component('CollapsibleTable', CollapsibleTable)
Vue.component('CollapsibleRow', CollapsibleRow)
Vue.component('CollapsibleRowArrow', CollapsibleRowArrow)

Vue.component('DataTable', DataTable)
Vue.component('DataTableTopHeaderRow', DataTableTopHeaderRow)
Vue.component('DataTableHeaderCell', DataTableHeaderCell)

Vue.component('StaticDataTable', StaticDataTable)

Vue.component('MobilePage', MobilePage)
Vue.component('MobileContentBox', MobileContentBox)
Vue.component('MobileNavBar', MobileNavBar)
Vue.component('MobileDrawer', MobileDrawer)
Vue.component('MobileDashboardButton', MobileDashboardButton)
Vue.component('MobileTable', MobileTable)
Vue.component('MobileTableCell', MobileTableCell)
Vue.component('MobileTableSectionHeader', MobileTableSectionHeader)
Vue.component('MobileTabbedPage', MobileTabbedPage)
Vue.component('MobileTabContent', MobileTabContent)
Vue.component('MobileTab', MobileTab)
Vue.component('MobileToggleButton', MobileToggleButton)

Vue.component('ContentBox', ContentBox)
Vue.component('ContentTitle', ContentTitle)
Vue.component('ContentSubTitle', ContentSubTitle)

new Vue({
  router,
  store,
  render: function (h) {
    return h(App)
  }
}).$mount('#app')
